import { useState, useCallback } from "react";
import "./EntityManagement.styles.scss";

// Component Imports
import DeleteModal from "@/components/Modals/DeleteModal/DeleteModal";
import AccountModal from "./components/AccountModal/AccountModal";
import AddEntityModal from "./components/AddEntityModal/AddEntityModal";
import EditPortfolioModal from "./components/EditPortfolioModal/EditPortfolioModal";

// Type Imports
import { IAlertMessage } from "@pulse-ui/types/components/AlertMessage";
import { IAGPortfolioDataRow } from "@pulse-ui/types/components/AGTable";

// SVG Imports
import LogoSVG from "@/svgs/pulse_prime_logo.svg?react";
import { Loading } from "@/layout";

// New refactored components and hooks
import { OrganizationHeader, EntityTable } from "./components";
import { ACCOUNT, PORTFOLIO } from "./utils";
import { useAlerts } from "@/hooks";
import {
  useAccountModalState,
  useAddEntityModal,
  useEditPortfolioModal,
  useEntityManagement,
} from "./hooks";
import { Alert, SvgIcon } from "@mui/material";
import {
  useDeleteAccountMutation,
  useDeletePortfolioMutation,
} from "./services/entityManagementApi";

interface DeleteModalState {
  open: boolean;
  item_id: string | null;
  item_name: string | null;
  entity_id: string | null;
  delete_type: typeof PORTFOLIO | typeof ACCOUNT | null;
  accounts_length: number | null;
}

/**
 * EntityManagement Page
 * Manages organizations, portfolios, and accounts
 * Refactored to use component composition and custom hooks
 */
const EntityManagement = () => {
  const { showError, showSuccess } = useAlerts();

  // Main page state and data fetching
  const {
    orgData,
    dataMap,
    loading,
    handleToggleRowExpanded,
    refetchAccountsForPortfolio,
  } = useEntityManagement();

  // Modal states
  const addModalState = useAddEntityModal();
  const editModalState = useEditPortfolioModal();
  const accountModalState = useAccountModalState();

  // Delete modal state
  const [deleteModalStatus, setDeleteModalStatus] = useState<DeleteModalState>({
    open: false,
    item_id: null,
    item_name: null,
    entity_id: null,
    delete_type: null,
    accounts_length: null,
  });

  // Alert messages
  const [alertMessages, setAlertMessages] = useState<IAlertMessage[]>([]);

  // RTK Query mutations
  const [deletePortfolio, { isLoading: isDeletingPortfolio }] =
    useDeletePortfolioMutation();
  const [deleteAccount, { isLoading: isDeletingAccount }] =
    useDeleteAccountMutation();

  // Combine all loading states for comprehensive loading indicator
  const isAnyLoading = loading || isDeletingPortfolio || isDeletingAccount;

  // Helper to show alerts
  const pushAlert = useCallback((alert: IAlertMessage) => {
    setAlertMessages((prev) => [...prev, alert]);
  }, []);

  // Helper to find account by ID across all portfolios
  const findAccountById = useCallback(
    (accountId: string) => {
      return Array.from(dataMap.values())
        .flatMap((p) => p.accounts)
        .find((a) => a.account_id === accountId);
    },
    [dataMap],
  );

  // Helper to reset delete modal
  const resetDeleteModal = useCallback(() => {
    setDeleteModalStatus({
      open: false,
      item_id: null,
      item_name: null,
      entity_id: null,
      delete_type: null,
      accounts_length: null,
    });
  }, []);

  // Delete handlers
  const handleDeletePortfolio = useCallback(async () => {
    const { item_id, entity_id, accounts_length } = deleteModalStatus;

    if (!entity_id || !item_id) {
      showError("Organization or Portfolio ID not found");
      return;
    }

    if (accounts_length && accounts_length > 0) {
      showError("Portfolio has accounts. Please delete accounts first.");
      resetDeleteModal();
      return;
    }

    try {
      await deletePortfolio({
        orgId: entity_id,
        portfolioId: item_id,
      }).unwrap();
      showSuccess("Portfolio deleted successfully");
    } catch (error: any) {
      showError(
        error?.data?.message || error?.message || "Failed to delete portfolio",
      );
    } finally {
      resetDeleteModal();
    }
  }, [
    deleteModalStatus,
    deletePortfolio,
    showError,
    showSuccess,
    resetDeleteModal,
  ]);

  const handleDeleteAccount = useCallback(async () => {
    const { item_id, entity_id } = deleteModalStatus;

    if (!entity_id || !item_id) {
      showError("Portfolio or Account ID not found");
      return;
    }

    try {
      await deleteAccount({
        portfolioId: entity_id,
        accountId: item_id,
      }).unwrap();
      showSuccess("Account deleted successfully");

      // Manually refetch accounts for the portfolio to update the UI
      await refetchAccountsForPortfolio(entity_id);
    } catch (error: any) {
      showError(
        error?.data?.message || error?.message || "Failed to delete account",
      );
    } finally {
      resetDeleteModal();
    }
  }, [
    deleteModalStatus,
    deleteAccount,
    showError,
    showSuccess,
    refetchAccountsForPortfolio,
    resetDeleteModal,
  ]);

  // Placeholder for fetchVenueAccountsForExpandedRows (kept for AccountModal compatibility)
  const fetchVenueAccountsForExpandedRows = useCallback(
    async (portfolios_map: Map<string, IAGPortfolioDataRow>) => {
      // This is a placeholder - AccountModal still uses old interface
      return portfolios_map;
    },
    [],
  );

  return (
    <div className="pulse-sidenav-adjusted-container pulse-bg-color--background-900 entity-management-top-container">
      <SvgIcon
        sx={{
          width: "100%",
          height: "100%",
          position: "absolute",
          top: 0,
          left: 0,
        }}
        children={<LogoSVG className="pulse-color--background-800" />}
      />

      {isAnyLoading && <Loading />}

      {alertMessages.length > 0 && (
        <div className="pulse-row pulse-mb-1">
          {alertMessages.map((alert) => (
            <Alert
              key={alert.id}
              onClose={() =>
                setAlertMessages((prev) =>
                  prev.filter((a) => a.id !== alert.id),
                )
              }
              severity={alert.status === 200 ? "success" : "error"}
              variant="filled"
              sx={{
                width: "100%",
                backgroundColor: alert.status === 200 ? "#0d732a" : "#8c060d",
              }}
            >
              {alert.message}
            </Alert>
          ))}
        </div>
      )}

      <OrganizationHeader
        orgData={orgData}
        onAddEntity={addModalState.openModal}
      />

      <EntityTable
        dataMap={dataMap}
        loading={isAnyLoading}
        onRowExpand={handleToggleRowExpanded}
        onEditPortfolio={(id, name) => editModalState.openModal(id, name)}
        onDeletePortfolio={(id, name, accountsLength) =>
          setDeleteModalStatus({
            open: true,
            item_id: id,
            item_name: name,
            entity_id: orgData?.org_id || null,
            delete_type: PORTFOLIO,
            accounts_length: accountsLength,
          })
        }
        onViewAccountDetails={(accountId, portfolioId) => {
          const account = findAccountById(accountId);
          if (account) {
            accountModalState.openModal(accountId, portfolioId);
          }
        }}
        onDeleteAccount={(accountId, name, portfolioId) =>
          setDeleteModalStatus({
            open: true,
            item_id: accountId,
            item_name: name,
            entity_id: portfolioId,
            delete_type: ACCOUNT,
            accounts_length: null,
          })
        }
      />

      <AddEntityModal
        {...addModalState}
        dataMap={dataMap}
        refetchAccountsForPortfolio={refetchAccountsForPortfolio}
      />

      <EditPortfolioModal
        open={editModalState.open}
        portfolioId={editModalState.portfolioId}
        portfolioName={editModalState.portfolioName}
        closeModal={editModalState.closeModal}
      />

      {accountModalState.accountId && accountModalState.portfolioId && (
        <AccountModal
          account_details={{
            account_id: accountModalState.accountId,
            portfolio_id: accountModalState.portfolioId,
            display_name:
              findAccountById(accountModalState.accountId)?.display_name || "",
          }}
          portfolio_id={accountModalState.portfolioId}
          dataMap={dataMap}
          setDataMap={() => {}}
          setLoading={() => {}}
          pushAlert={pushAlert}
          setSelectedAccountDetails={() => accountModalState.closeModal()}
          fetchVenueAccountsForExpandedRows={fetchVenueAccountsForExpandedRows}
        />
      )}

      <DeleteModal
        open={deleteModalStatus.open}
        item_name={deleteModalStatus.item_name}
        handleClose={resetDeleteModal}
        handleSubmit={
          deleteModalStatus.delete_type === PORTFOLIO
            ? handleDeletePortfolio
            : handleDeleteAccount
        }
      />
    </div>
  );
};

export default EntityManagement;
